import assert from '../engine/assert'
import Faction from '../engine/faction'
import Game from '../engine/game'
import Hex from '../engine/hex'
import HexMap, { IMap, Terrain } from '../engine/map'
import { IUnitType } from '../engine/unit'
import { ILevelDefinition } from './levels'

const CLUSTERING_PROBABILITY = .75
const REC_LIMIT = 100000
const CONNECTED_TERRAINS: Set<Terrain> = new Set([
  Terrain.Ground,
  Terrain.Water,
  Terrain.Forest,
])

function tryUntil<T>(
  f: () => T, predicate: (item: T) => boolean, limit = REC_LIMIT,
) {
  while (true) {
    assert(limit > 0, 'recursion exceeded. Revisit your parameters')
    const item = f()
    if (predicate(item)) {
      return item
    }

    limit--
  }
}

function getRandomCell(map: IMap) {
  const { cells } = map
  return cells[Math.floor(Math.random() * cells.length)]
}

function getRandomNeighbor(map: IMap, hex: Hex) {
  const neighbors = hex.neighbors.filter(map.isIn)
  return neighbors[Math.floor(Math.random() * neighbors.length)]
}

function isConnectedTerrain(terrain: Terrain) {
  return CONNECTED_TERRAINS.has(terrain)
}

function getConnectedGround(map: IMap) {
  const groundCells = map.cells.filter(c => isConnectedTerrain(c.terrain))
  const connected = new Set<string>()
  const toProcess = groundCells.length > 0 ? [groundCells[0].pos] : []

  while (toProcess.length > 0) {
    const pos = toProcess.splice(0, 1)[0]
    const idx = pos.toString()
    if (connected.has(idx)) {
      continue
    }

    connected.add(idx)
    pos.neighbors
      .filter(map.isIn)
      .map(map.cellAt)
      .filter(c => isConnectedTerrain(c.terrain))
      .forEach(c => toProcess.push(c.pos))
  }

  return connected
}

function findBridgePath(map: IMap, connected: Set<string>, target: Hex) {
  const toProcess: Array<[Hex, Hex[]]> = map.cells
    .filter(c => connected.has(c.pos.toString()))
    .map(c => [c.pos, []] as [Hex, Hex[]])
  const visited = new Set<string>(connected)

  while (toProcess.length > 0) {
    const [pos, path] = toProcess.splice(0, 1)[0]
    if (pos.equals(target)) {
      return path
    }

    pos.neighbors
      .filter(map.isIn)
      .filter(n => !visited.has(n.toString()))
      .forEach(n => {
        visited.add(n.toString())
        toProcess.push([n, [...path, n]] as [Hex, Hex[]])
      })
  }

  assert(false, 'Could not connect ground regions')
  return []
}

function connectGroundRegions(map: IMap) {
  while (true) {
    const connected = getConnectedGround(map)
    const disconnected = map.cells.find(c =>
      isConnectedTerrain(c.terrain) && !connected.has(c.pos.toString()),
    )

    if (!disconnected) {
      return
    }

    findBridgePath(map, connected, disconnected.pos)
      .forEach(pos => map.cellAt(pos).terrain = Terrain.Ground)
  }
}

export default function createLevel(
  def: ILevelDefinition, party: IUnitType[],
): Game {
  const map = new HexMap(def.map.size, [])

  // add terrains
  def.map.terrains.forEach((amount, terrain) => {
    let curCell = tryUntil(
      () => getRandomCell(map),
      c => c.terrain === Terrain.Ground,
    )
    while (amount > 0) {
      let tempPos
      // determines if the next cell should be anywhere else or attached to
      // the previous one
      const cellFinder = Math.random() > CLUSTERING_PROBABILITY
        ? () => getRandomCell(map)
        : () => {
          tempPos = getRandomNeighbor(map, curCell.pos)
          return map.cellAt(tempPos)
        }
      curCell = tryUntil(cellFinder, c => c.terrain === Terrain.Ground)

      curCell.terrain = terrain
      amount--
    }
  })
  connectGroundRegions(map)

  const fa = new Faction('Greens', '#00A000')
  const fb = new Faction('Reds', '#A00000')

  const game = new Game({ factions: [fa, fb], map })

  // add friendly units
  party.forEach(unit => {
    let tempPos = def.partyDeployOrigins[0]
    tryUntil(
      () => {
        tempPos = getRandomNeighbor(map, tempPos)
        const c = map.cellAt(tempPos)
        return c
      },
      c => c.terrain === Terrain.Ground && !c.thing,
    )

    game.addUnit({ factionId: fa.id, pos: tempPos, type: unit })
  })

  // add enemy units
  def.opponents.forEach((amount, unit) => {
    while (amount > 0) {
      let tempPos = def.partyDeployOrigins[1]
      tryUntil(
        () => {
          tempPos = getRandomNeighbor(map, tempPos)
          const c = map.cellAt(tempPos)
          return c
        },
        c => c.terrain === Terrain.Ground && !c.thing,
      )

      game.addUnit({ factionId: fb.id, pos: tempPos, type: unit })
      amount--
    }
  })

  return game
}



// WEBPACK FOOTER //
// ./src/content/createLevel.ts